import React from "react";
import { useStytchMemberSession } from "@stytch/react/b2b";
import { usePermissionLevel, type PermissionLevel } from "../../hooks/usePermissionLevel";

interface AdminOnlyProps {
    children: React.ReactNode;
    /**
     * Floor level required to render `children`. Defaults to "admin" — the
     * name predates the `minLevel` prop and every pre-existing caller relies
     * on that default, so it's kept rather than renaming the component.
     */
    minLevel?: PermissionLevel;
}

const RANK: Record<PermissionLevel, number> = { member: 0, manager: 1, admin: 2 };

/**
 * AdminOnly — render-gating wrapper for admin-only (or, with `minLevel`,
 * manager-or-above) UI.
 *
 * Behavior:
 *   - !isInitialized → null (fail-closed; AuthWrapper's "Connecting..." spinner
 *     covers the page-load case visually, but defaulting to null here keeps
 *     this wrapper conservative for any future caller outside AuthWrapper).
 *   - isInitialized && RANK[level] < RANK[minLevel] → null
 *   - isInitialized && RANK[level] >= RANK[minLevel] → {children}
 *
 * Fail-closed is deliberate over fail-open — gated chrome must NEVER render
 * speculatively, even for one frame. The principle outweighs the tiny UX cost
 * of a one-frame blank for legitimate users on cold load.
 *
 * Frontend gating is UX convenience only. The server-side MinLevelGuard is
 * the actual security boundary.
 */
const AdminOnly: React.FC<AdminOnlyProps> = ({ children, minLevel = "admin" }) => {
    const { isInitialized } = useStytchMemberSession();
    const { level } = usePermissionLevel();

    if (!isInitialized) {
        return null;
    }
    if (RANK[level] < RANK[minLevel]) {
        return null;
    }
    return <>{children}</>;
};

export default AdminOnly;
